import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faShieldAlt,
  faEnvelope,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faUsers,
  faTachometerAlt,
  faGlobe,
  faArrowTrendUp,
  faArrowTrendDown,
  faTimesCircle,
  faExclamationCircle,
  faBan,
  faTag,
  faDesktop,
  faDatabase,
  faShieldVirus,
} from "@fortawesome/free-solid-svg-icons";
import "../css/Common.css";
import "./css/AdminDashboard.css";
import RefreshButton from "../common/RefreshButton";
import LoadingSpinner from "../common/LoadingSpinner";
import AdminDashboardAPI from "../services/AdminDashboardAPI";
import AuthenticationFailedAttemptsAPI from "../services/AuthenticationFailedAttemptsAPI";
import { formatTimeOnly } from "../utils/DateTimeUtil";

// Import analytics components
import AuthenticationFailedAttempts from "./components/AuthenticationFailedAttempts";
import UserAdmin from "./components/UserAdmin";
import SystemErrors from "./components/SystemErrors";
import PromotionsAdmin from "./components/PromotionsAdmin";
import UserSessionsAdmin from "./components/UserSessionsAdmin";
import FccCacheAdmin from "./components/FccCacheAdmin";
import AdminBlocklistReview from "./AdminBlocklistReview";

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState(24); // Add time range state

  // Load dashboard overview data
  const loadDashboardData = async (timeRange = selectedTimeRange) => {
    try {
      setLoading(true);
      setError(null);

      // Load main dashboard data and failed attempts data in parallel
      // Pass timeRange parameter to both API calls
      const [mainDashboardData, failedAttemptsData] = await Promise.all([
        AdminDashboardAPI.getDashboardOverview(timeRange),
        AuthenticationFailedAttemptsAPI.getDashboard(timeRange),
      ]);

      // Combine the data
      const combinedData = {
        ...mainDashboardData,
        failedAttemptsAnalytics: failedAttemptsData,
      };
      setDashboardData(combinedData);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("[AdminDashboard] Error loading dashboard data:", err);
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Calculate overall system health score
  const calculateSystemHealth = (data) => {
    let score = 100;

    // Deduct points for failures
    if (data?.loginAnalytics?.successRate < 90) score -= 10;
    if (data?.simulationAnalytics?.executionSuccessRate < 90) score -= 10;
    if (data?.emailAnalytics?.processingSuccessRate < 90) score -= 10;

    // Deduct points for security concerns
    if (data?.failedAttemptsAnalytics?.suspiciousIPs > 5) score -= 15;
    if (data?.failedAttemptsAnalytics?.recentFailures > 50) score -= 10;

    return Math.max(score, 0);
  };

  // Handle time range change
  const handleTimeRangeChange = (hours) => {
    setSelectedTimeRange(hours);
    loadDashboardData(hours);
  };

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
  };

  // Auto-refresh every 5 minutes for dashboard data
  useEffect(() => {
    loadDashboardData();

    // Full dashboard refresh every 5 minutes
    const fullRefreshInterval = setInterval(
      () => {
        loadDashboardData();
      },
      5 * 60 * 1000,
    );

    return () => {
      clearInterval(fullRefreshInterval);
    };
  }, [selectedTimeRange]); // Add dependency on selectedTimeRange

  // Format numbers for display
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  // Get status color based on value
  const getStatusColor = (value) => {
    if (value >= 90) return "#10b981"; // green
    if (value >= 70) return "#f59e0b"; // yellow
    return "#ef4444"; // red
  };

  // Get trend indicator with icon and color
  const getTrendIndicator = (current, previous) => {
    const change = current - previous;
    if (Math.abs(change) < 1)
      return { icon: null, color: "#6b7280", text: "0%" };

    return {
      icon: change > 0 ? faArrowTrendUp : faArrowTrendDown,
      color: change > 0 ? "#10b981" : "#ef4444",
      text: `${change > 0 ? "+" : ""}${change.toFixed(1)}%`,
    };
  };

  const renderOverviewCards = () => {
    if (!dashboardData) return null;

    const {
      loginAnalytics,
      simulationAnalytics,
      emailAnalytics,
      blogAnalytics,
    } = dashboardData;

    const systemHealthScore = calculateSystemHealth(dashboardData);

    return (
      <div className="admin-dashboard-overview">
        {/* Key Performance Indicators */}
        <div className="kpi-grid">
          <div className="kpi-card primary">
            <div className="kpi-header">
              <div className="kpi-icon">
                <FontAwesomeIcon icon={faTachometerAlt} />
              </div>
              <div className="kpi-info">
                <h3>System Health</h3>
                <p>Overall performance score</p>
              </div>
            </div>
            <div className="kpi-value">
              <span
                className="kpi-number"
                style={{ color: getStatusColor(systemHealthScore) }}
              >
                {systemHealthScore}
              </span>
              <span className="kpi-unit">/ 100</span>
            </div>
            <div className="kpi-footer">
              <div className="kpi-progress">
                <div
                  className="kpi-progress-bar"
                  style={{
                    width: `${systemHealthScore}%`,
                    backgroundColor: getStatusColor(systemHealthScore),
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <div className="kpi-icon">
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <div className="kpi-info">
                <h3>Login Success</h3>
                <p>Authentication rate</p>
              </div>
            </div>
            <div className="kpi-value">
              <span className="kpi-number">
                {loginAnalytics?.successRate?.toFixed(1) || "0.0"}
              </span>
              <span className="kpi-unit">%</span>
            </div>
            <div className="kpi-footer">
              <span className="kpi-subtext">
                {formatNumber(loginAnalytics?.totalLoginAttempts || 0)} total
                attempts
              </span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <div className="kpi-icon">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="kpi-info">
                <h3>Simulations</h3>
                <p>Training executed</p>
              </div>
            </div>
            <div className="kpi-value">
              <span className="kpi-number">
                {formatNumber(simulationAnalytics?.totalExecuted || 0)}
              </span>
            </div>
            <div className="kpi-footer">
              <span className="kpi-subtext">
                {simulationAnalytics?.executionSuccessRate?.toFixed(1) || "0.0"}
                % success rate
              </span>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-header">
              <div className="kpi-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <div className="kpi-info">
                <h3>Email Processing</h3>
                <p>Analysis completed</p>
              </div>
            </div>
            <div className="kpi-value">
              <span className="kpi-number">
                {formatNumber(emailAnalytics?.totalProcessed || 0)}
              </span>
            </div>
            <div className="kpi-footer">
              <span className="kpi-subtext">
                {emailAnalytics?.processingSuccessRate?.toFixed(1) || "0.0"}%
                success rate
              </span>
            </div>
          </div>

          <div className="kpi-card security-threat">
            <div className="kpi-header">
              <div className="kpi-icon">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <div className="kpi-info">
                <h3>Failed Attempts</h3>
                <p>Authentication security</p>
              </div>
            </div>
            <div className="kpi-value">
              <span className="kpi-number">
                {formatNumber(
                  dashboardData?.failedAttemptsAnalytics?.recentFailures || 0,
                )}
              </span>
              <span className="kpi-unit">attempts</span>
            </div>
            <div className="kpi-footer">
              <div className="kpi-metrics">
                <span className="kpi-subtext">
                  <FontAwesomeIcon icon={faBan} />
                  &nbsp;&nbsp;
                  {formatNumber(
                    dashboardData?.failedAttemptsAnalytics?.suspiciousIPs || 0,
                  )}{" "}
                  suspicious IPs
                </span>
                {dashboardData?.failedAttemptsAnalytics?.topFailureReason && (
                  <span className="kpi-subtext secondary">
                    Top reason:{" "}
                    {dashboardData.failedAttemptsAnalytics.topFailureReason}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Analytics Grid */}
        <div className="overview-grid">
          {/* Security & Authentication Card */}
          <div className="overview-card login-activity enhanced">
            <div className="card-header">
              <FontAwesomeIcon icon={faShieldAlt} className="card-icon" />
              <div className="card-title">
                <h3>Security & Authentication</h3>
                <span className="card-subtitle">User login analytics</span>
              </div>
            </div>
            <div className="card-content">
              <div className="metric-highlight">
                <div className="metric-highlight-value">
                  <span
                    style={{
                      color: getStatusColor(loginAnalytics?.successRate || 0),
                    }}
                  >
                    {loginAnalytics?.successRate?.toFixed(1) || 0}%
                  </span>
                </div>
                <div className="metric-highlight-label">Success Rate</div>
                <div className="metric-highlight-trend">
                  {getTrendIndicator(loginAnalytics?.successRate || 0, 95)
                    ?.icon && (
                    <>
                      <FontAwesomeIcon
                        icon={
                          getTrendIndicator(
                            loginAnalytics?.successRate || 0,
                            95,
                          ).icon
                        }
                        style={{
                          color: getTrendIndicator(
                            loginAnalytics?.successRate || 0,
                            95,
                          ).color,
                        }}
                      />
                      <span
                        style={{
                          color: getTrendIndicator(
                            loginAnalytics?.successRate || 0,
                            95,
                          ).color,
                        }}
                      >
                        {
                          getTrendIndicator(
                            loginAnalytics?.successRate || 0,
                            95,
                          ).text
                        }
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="metric-grid-small">
                <div className="metric-item-small">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="metric-icon success"
                  />
                  <div>
                    <div className="metric-value small">
                      {formatNumber(loginAnalytics?.successfulLogins || 0)}
                    </div>
                    <div className="metric-label small">Successful</div>
                  </div>
                </div>
                <div className="metric-item-small">
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    className="metric-icon warning"
                  />
                  <div>
                    <div className="metric-value small">
                      {formatNumber(loginAnalytics?.failedLogins || 0)}
                    </div>
                    <div className="metric-label small">Failed</div>
                  </div>
                </div>
                <div className="metric-item-small">
                  <FontAwesomeIcon icon={faUsers} className="metric-icon" />
                  <div>
                    <div className="metric-value small">
                      {formatNumber(loginAnalytics?.uniqueUsersLoggedIn || 0)}
                    </div>
                    <div className="metric-label small">Unique Users</div>
                  </div>
                </div>
              </div>

              {/* Failed Attempts Security Section */}
            </div>
          </div>

          {/* Simulation Analytics Card */}
          <div className="overview-card simulation-status enhanced">
            <div className="card-header">
              <FontAwesomeIcon icon={faChartLine} className="card-icon" />
              <div className="card-title">
                <h3>Simulation Engine</h3>
                <span className="card-subtitle">Training and execution</span>
              </div>
            </div>
            <div className="card-content">
              <div className="metric-highlight">
                <div className="metric-highlight-value">
                  <span
                    style={{
                      color: getStatusColor(
                        simulationAnalytics?.executionSuccessRate || 0,
                      ),
                    }}
                  >
                    {simulationAnalytics?.executionSuccessRate?.toFixed(1) || 0}
                    %
                  </span>
                </div>
                <div className="metric-highlight-label">
                  Execution Success Rate
                </div>
                <div className="metric-highlight-trend">
                  {getTrendIndicator(
                    simulationAnalytics?.executionSuccessRate || 0,
                    92,
                  )?.icon && (
                    <>
                      <FontAwesomeIcon
                        icon={
                          getTrendIndicator(
                            simulationAnalytics?.executionSuccessRate || 0,
                            92,
                          ).icon
                        }
                        style={{
                          color: getTrendIndicator(
                            simulationAnalytics?.executionSuccessRate || 0,
                            92,
                          ).color,
                        }}
                      />
                      <span
                        style={{
                          color: getTrendIndicator(
                            simulationAnalytics?.executionSuccessRate || 0,
                            92,
                          ).color,
                        }}
                      >
                        {
                          getTrendIndicator(
                            simulationAnalytics?.executionSuccessRate || 0,
                            92,
                          ).text
                        }
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="metric-grid-small">
                <div className="metric-item-small">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="metric-icon success"
                  />
                  <div>
                    <div className="metric-value small">
                      {formatNumber(simulationAnalytics?.totalExecuted || 0)}
                    </div>
                    <div className="metric-label small">Executed</div>
                  </div>
                </div>
                <div className="metric-item-small">
                  <FontAwesomeIcon icon={faClock} className="metric-icon" />
                  <div>
                    <div className="metric-value small">
                      {formatNumber(simulationAnalytics?.totalScheduled || 0)}
                    </div>
                    <div className="metric-label small">Scheduled</div>
                  </div>
                </div>
                <div className="metric-item-small">
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    className="metric-icon warning"
                  />
                  <div>
                    <div className="metric-value small">
                      {formatNumber(simulationAnalytics?.totalFailed || 0)}
                    </div>
                    <div className="metric-label small">Failed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Email Processing Card */}
          <div className="overview-card email-analysis enhanced">
            <div className="card-header">
              <FontAwesomeIcon icon={faEnvelope} className="card-icon" />
              <div className="card-title">
                <h3>Email Safety Analysis</h3>
                <span className="card-subtitle">Analysis and handling</span>
              </div>
            </div>
            <div className="card-content">
              <div className="metric-highlight">
                <div className="metric-highlight-value">
                  <span
                    style={{
                      color: getStatusColor(
                        emailAnalytics?.processingSuccessRate || 0,
                      ),
                    }}
                  >
                    {emailAnalytics?.processingSuccessRate?.toFixed(1) || 0}%
                  </span>
                </div>
                <div className="metric-highlight-label">
                  Processing Success Rate
                </div>
                <div className="metric-highlight-trend">
                  {getTrendIndicator(
                    emailAnalytics?.processingSuccessRate || 0,
                    95,
                  )?.icon && (
                    <>
                      <FontAwesomeIcon
                        icon={
                          getTrendIndicator(
                            emailAnalytics?.processingSuccessRate || 0,
                            95,
                          ).icon
                        }
                        style={{
                          color: getTrendIndicator(
                            emailAnalytics?.processingSuccessRate || 0,
                            95,
                          ).color,
                        }}
                      />
                      <span
                        style={{
                          color: getTrendIndicator(
                            emailAnalytics?.processingSuccessRate || 0,
                            95,
                          ).color,
                        }}
                      >
                        {
                          getTrendIndicator(
                            emailAnalytics?.processingSuccessRate || 0,
                            95,
                          ).text
                        }
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="metric-grid-small">
                <div className="metric-item-small">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="metric-icon success"
                  />
                  <div>
                    <div className="metric-value small">
                      {formatNumber(emailAnalytics?.totalProcessed || 0)}
                    </div>
                    <div className="metric-label small">Processed</div>
                  </div>
                </div>
                <div className="metric-item-small">
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    className="metric-icon warning"
                  />
                  <div>
                    <div className="metric-value small">
                      {formatNumber(emailAnalytics?.totalFailed || 0)}
                    </div>
                    <div className="metric-label small">Failed</div>
                  </div>
                </div>
                <div className="metric-item-small">
                  <FontAwesomeIcon icon={faGlobe} className="metric-icon" />
                  <div>
                    <div className="metric-value small">
                      {formatNumber(emailAnalytics?.averageProcessingTime || 0)}
                    </div>
                    <div className="metric-label small">Avg Time (ms)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "failed-attempts":
        return <AuthenticationFailedAttempts />;
      case "user-roles":
        return <UserAdmin />;
      case "system-errors":
        return <SystemErrors />;
      case "promotions":
        return <PromotionsAdmin />;
      case "user-sessions":
        return <UserSessionsAdmin />;
      case "fcc-cache":
        return <FccCacheAdmin />;
      case "blocklist-review":
        return <AdminBlocklistReview />;
      default:
        return renderOverviewCards();
    }
  };

  if (loading && !dashboardData) {
    return (
      <div className="admin-dashboard loading">
        <LoadingSpinner message="Loading dashboard data..." />
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="admin-dashboard error">
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={loadDashboardData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const systemHealthScore = calculateSystemHealth(dashboardData || {});

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-dashboard-header">
        <div className="header-content">
          <div className="header-main">
            <h1>
              <FontAwesomeIcon icon={faTachometerAlt} className="header-icon" />
              Admin Dashboard
            </h1>
            <p>Comprehensive platform monitoring and analytics</p>
          </div>
          <div className="header-controls-center">
            <div className="time-range-selector">
              <label>Time Range:</label>
              <select
                value={selectedTimeRange}
                onChange={(e) => handleTimeRangeChange(Number(e.target.value))}
              >
                <option value={1}>Last Hour</option>
                <option value={6}>Last 6 Hours</option>
                <option value={24}>Last 24 Hours</option>
                <option value={168}>Last Week</option>
              </select>
            </div>
          </div>
          <div className="header-status">
            <div className="header-controls">
              <div className="last-updated">
                Last updated:{" "}
                {lastUpdated ? formatTimeOnly(lastUpdated, true) : "N/A"}
              </div>
              <RefreshButton onClick={handleRefresh} loading={refreshing} />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="admin-dashboard-nav">
        <button
          className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <FontAwesomeIcon icon={faTachometerAlt} />
          <span>Overview</span>
          <div className="tab-indicator healthy"></div>
        </button>
        <button
          className={`tab-button ${
            activeTab === "failed-attempts" ? "active" : ""
          }`}
          onClick={() => setActiveTab("failed-attempts")}
        >
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>Failed Attempts</span>
          <div className="tab-indicator warning"></div>
        </button>
        <button
          className={`tab-button ${activeTab === "user-roles" ? "active" : ""}`}
          onClick={() => setActiveTab("user-roles")}
        >
          <FontAwesomeIcon icon={faUsers} />
          <span>User Administration</span>
          <div className="tab-indicator healthy"></div>
        </button>
        <button
          className={`tab-button ${activeTab === "system-errors" ? "active" : ""}`}
          onClick={() => setActiveTab("system-errors")}
        >
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>System Errors</span>
          <div className="tab-indicator warning"></div>
        </button>
        <button
          className={`tab-button ${activeTab === "promotions" ? "active" : ""}`}
          onClick={() => setActiveTab("promotions")}
        >
          <FontAwesomeIcon icon={faTag} />
          <span>Promotions</span>
          <div className="tab-indicator healthy"></div>
        </button>
        <button
          className={`tab-button ${activeTab === "user-sessions" ? "active" : ""}`}
          onClick={() => setActiveTab("user-sessions")}
        >
          <FontAwesomeIcon icon={faDesktop} />
          <span>User Sessions</span>
          <div className="tab-indicator healthy"></div>
        </button>
        <button
          className={`tab-button ${activeTab === "fcc-cache" ? "active" : ""}`}
          onClick={() => setActiveTab("fcc-cache")}
        >
          <FontAwesomeIcon icon={faDatabase} />
          <span>FCC Cache</span>
          <div className="tab-indicator healthy"></div>
        </button>
        <button
          className={`tab-button ${
            activeTab === "blocklist-review" ? "active" : ""
          }`}
          onClick={() => setActiveTab("blocklist-review")}
        >
          <FontAwesomeIcon icon={faShieldVirus} />
          <span>Blocklist Review</span>
          <div className="tab-indicator warning"></div>
        </button>
      </div>

      {/* Tab Content */}
      <div className="admin-dashboard-content">{renderTabContent()}</div>
    </div>
  );
}

export default AdminDashboard;
